import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../lib/utils';

/**
 * PackageBadge — kisméretű csomag-típus jelző a Worksheet / Rental / Sales
 * UI-kban (ADR-118 + Addendum 1, E-105 Phase 2 Story S3).
 *
 * A 6 `PackageType` enum-érték (RENTAL, SERVICE, SALES, MIXED, PROMOTIONAL,
 * FRANCHISE) mindegyikéhez distinct szín-páros (light + dark mode, WCAG AA
 * kontraszt). Kiegészítő állapotok:
 * - `drift`: a csomag módosult a rögzítés óta → piros ring overlay
 *   (ADR-117 D-4 soft-warn banner-rel együtt használható)
 * - `expired`: a csomag `validTo` dátuma lejárt → áthúzott + kisebb opacitás
 *   (ADR-117 D-5 explicit reselection jelzés)
 *
 * A komponens kontrolálatlan (nincs state), presentational csak.
 */

export type PackageBadgeType =
  | 'RENTAL'
  | 'SERVICE'
  | 'SALES'
  | 'MIXED'
  | 'PROMOTIONAL'
  | 'FRANCHISE';

const packageBadgeVariants = cva(
  'inline-flex items-center gap-1 rounded-md border border-transparent font-medium transition-colors',
  {
    variants: {
      packageType: {
        RENTAL: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100',
        SERVICE: 'bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100',
        SALES: 'bg-sky-100 text-sky-900 dark:bg-sky-900 dark:text-sky-100',
        MIXED: 'bg-purple-100 text-purple-900 dark:bg-purple-900 dark:text-purple-100',
        PROMOTIONAL: 'bg-pink-100 text-pink-900 dark:bg-pink-900 dark:text-pink-100',
        FRANCHISE: 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-1 text-sm',
      },
      drift: {
        true: 'ring-2 ring-red-500 ring-offset-1 dark:ring-offset-slate-900',
        false: '',
      },
      expired: {
        true: 'opacity-60 line-through',
        false: '',
      },
    },
    defaultVariants: {
      size: 'md',
      drift: false,
      expired: false,
    },
  }
);

export interface PackageBadgeProps
  extends
    Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'>,
    VariantProps<typeof packageBadgeVariants> {
  /** A csomag típusa — 6 PackageType enum értékből egy (ADR-118 D-1). */
  packageType: PackageBadgeType;
  /**
   * A megjelenített címke. Ha nincs megadva, az enum érték jelenik meg
   * (`'RENTAL'`, `'SERVICE'`, stb.). i18n-fordított címkét kívülről kell
   * beadni (`t('package.type.RENTAL')`), ez a komponens presentational.
   */
  label?: string;
  /** Drift állapot: a csomag módosult a rögzítés óta (ADR-117 D-4). */
  drift?: boolean;
  /** Lejárt állapot: `validTo` múltban van (ADR-117 D-5). */
  expired?: boolean;
  /** Méret variáns. Alapérték: `'md'`. */
  size?: 'sm' | 'md';
}

/**
 * WCAG-barát accessible label — a screen reader user drift és expired
 * állapotokat is hallja, nem csak vizuálisan látja.
 */
function ariaLabelFor(type: PackageBadgeType, drift: boolean, expired: boolean): string {
  const parts = [`${type} csomag`];
  if (drift) parts.push('módosult a rögzítés óta');
  if (expired) parts.push('lejárt');
  return parts.join(', ');
}

export function PackageBadge({
  packageType,
  label,
  drift = false,
  expired = false,
  size = 'md',
  className,
  ...props
}: PackageBadgeProps) {
  return (
    <span
      className={cn(packageBadgeVariants({ packageType, size, drift, expired }), className)}
      role="status"
      aria-label={ariaLabelFor(packageType, drift, expired)}
      data-package-type={packageType}
      data-drift={drift || undefined}
      data-expired={expired || undefined}
      {...props}
    >
      {label ?? packageType}
    </span>
  );
}

export { packageBadgeVariants };
